import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion';
import {metrics, categories} from '../data/metrics';
import {FiArrowLeft, FiCheckCircle, FiAlertCircle, FiInfo, FiTarget, FiActivity, FiBookOpen, FiZap, FiFileText} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import clsx from 'clsx';

const CalculatorPage = () => {
  const {metricId} = useParams();
  const navigate = useNavigate();
  const metric = metrics.find(m => m.id === metricId);
  const category = metric ? categories.find(c => c.id === metric.categoryId) : null;

  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const hasCalculator = metric?.inputs && metric.inputs.length > 0 && !!metric.calc;

  useEffect(() => {
    if (metric) {
      if (hasCalculator) {
        const initialInputs = {};
        metric.inputs.forEach(input => {
          initialInputs[input.id] = '';
        });
        setInputs(initialInputs);
        setResult(null);
        setActiveTab('calculator');
      } else {
        setActiveTab('overview');
      }
    }
  }, [metric, hasCalculator]);

  if (!metric) return <div className="p-10 text-center">Metric Not Found</div>;

  const handleInputChange = (id, value) => {
    setInputs(prev => ({...prev, [id]: parseFloat(value) || ''}));
  };

  const calculate = (e) => {
    e.preventDefault();
    const numValues = {};
    for (const key in inputs) {
      numValues[key] = parseFloat(inputs[key]) || 0;
    }
    const res = metric.calc(numValues);
    setResult(res);
  };

  const formatResult = (val, fmt) => {
    if (val === undefined || val === null || isNaN(val)) return '—';
    if (fmt === 'currency') return '$' + val.toLocaleString('en-US', {maximumFractionDigits: 0});
    if (fmt === 'currency4') return '$' + val.toFixed(4);
    if (fmt === 'percent1') return val.toFixed(1) + '%';
    if (fmt === 'percent2') return val.toFixed(2) + '%';
    if (fmt === 'decimal0') return val.toFixed(0);
    if (fmt === 'decimal1') return val.toFixed(1);
    if (fmt === 'decimal2') return val.toFixed(2);
    return val.toLocaleString();
  };

  const getBenchmarkStatus = (val) => {
    if (!metric.benchmark || !metric.benchmark.type) return 'neutral';
    const {type, low, high} = metric.benchmark;
    if (type === 'higher') {
      if (val >= low) return 'good';
      if (val >= low * 0.85) return 'warning';
      return 'bad';
    }
    if (type === 'lower') {
      if (val <= high) return 'good';
      if (val <= high * 1.3) return 'warning';
      return 'bad';
    }
    if (type === 'range') {
      if (val >= low && val <= high) return 'good';
      return 'warning';
    }
    return 'neutral';
  };

  return (
    <div className="max-w-6xl mx-auto pb-12">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-slate-500 hover:text-jeruel-blue mb-6 transition-colors font-medium text-sm group"
      >
        <div className="bg-white border border-jeruel-grayblue rounded-full p-1.5 mr-2 group-hover:border-jeruel-blue">
          <SafeIcon icon={FiArrowLeft} />
        </div>
        Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-jeruel-grayblue overflow-hidden">
            <div className="p-8 border-b border-slate-100 relative overflow-hidden">
              <div className={clsx("absolute top-0 right-0 w-32 h-32 transform translate-x-8 -translate-y-8 rounded-full opacity-10", category?.bg.replace('bg-', 'bg-'))} />
              <div className="flex items-center gap-3 mb-4">
                <span className={clsx("px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wide border", category?.bg, category?.color, category?.border)}>
                  {category?.name}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-4">{metric.name}</h1>
              <p className="text-slate-600 text-lg leading-relaxed">{metric.definition}</p>
            </div>

            <div className="flex border-b border-slate-100 bg-slate-50/50 overflow-x-auto">
              {[
                hasCalculator && {id: 'calculator', label: 'Calculator', icon: FiActivity},
                {id: 'overview', label: 'Details & Formula', icon: FiBookOpen},
                {id: 'insight', label: 'Insights & Objectives', icon: FiZap},
              ].filter(Boolean).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    "flex items-center px-6 py-4 text-sm font-bold transition-all relative whitespace-nowrap",
                    activeTab === tab.id
                      ? "text-jeruel-blue bg-white"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-100/50"
                  )}
                >
                  <SafeIcon icon={tab.icon} className="mr-2 text-lg" />
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-jeruel-blue" />
                  )}
                </button>
              ))}
            </div>

            <div className="p-8">
              {activeTab === 'calculator' && hasCalculator && (
                <motion.div initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}}>
                  <div className="bg-jeruel-blue rounded-xl p-6 mb-8 text-center shadow-inner relative overflow-hidden">
                    <div className="absolute inset-0 bg-jeruel-light/10 pattern-grid-lg" />
                    <p className="text-white/60 text-xs uppercase tracking-widest font-bold mb-2">Formula</p>
                    <code className="text-white font-mono text-lg md:text-xl font-bold tracking-wide relative z-10 block">
                      {metric.formulaDisplay}
                    </code>
                  </div>

                  <form onSubmit={calculate} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {metric.inputs.map(input => (
                        <div key={input.id}>
                          <label className="block text-sm font-bold text-slate-700 mb-2">
                            {input.label}
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              step="any"
                              value={inputs[input.id] || ''}
                              onChange={(e) => handleInputChange(input.id, e.target.value)}
                              className="w-full pl-4 pr-12 py-3 bg-white border border-jeruel-grayblue rounded-lg focus:ring-2 focus:ring-jeruel-blue focus:border-jeruel-blue outline-none transition-all placeholder-slate-300 font-medium text-slate-900 shadow-sm"
                              placeholder="0"
                            />
                            <div className="absolute right-0 top-0 bottom-0 flex items-center pr-4 pointer-events-none">
                              <span className="text-slate-400 text-xs font-bold bg-jeruel-soft px-2 py-1 rounded">
                                {input.unit}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-jeruel-blue hover:bg-jeruel-light text-white font-bold py-4 rounded-xl shadow-lg shadow-jeruel-blue/20 transition-all transform hover:-translate-y-0.5 flex items-center justify-center text-lg"
                    >
                      <SafeIcon icon={FiActivity} className="mr-2" />
                      Calculate Result
                    </button>
                  </form>
                </motion.div>
              )}

              {activeTab === 'overview' && (
                <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="space-y-8">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3 flex items-center">
                      <SafeIcon icon={FiInfo} className="mr-2 text-jeruel-blue" />
                      Full Definition
                    </h3>
                    <p className="text-slate-600 leading-relaxed bg-jeruel-soft p-4 rounded-lg border border-jeruel-grayblue">
                      {metric.definition}
                    </p>
                  </div>

                  {metric.formula && (
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">Formula Breakdown</h3>
                      <div className="bg-slate-800 text-slate-200 p-4 rounded-lg font-mono text-sm border-l-4 border-jeruel-blue">
                        {metric.formula}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'insight' && (
                <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="space-y-8">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3 flex items-center">
                      <SafeIcon icon={FiZap} className="mr-2 text-amber-500" />
                      Expert Insight
                    </h3>
                    <div className="bg-amber-50 border border-amber-100 p-5 rounded-xl text-slate-700 leading-relaxed">
                      {metric.insight}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3 flex items-center">
                      <SafeIcon icon={FiTarget} className="mr-2 text-jeruel-blue" />
                      Strategic Objectives
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {metric.objectives}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <AnimatePresence mode="wait">
            {hasCalculator ? (
              result ? (
                <motion.div
                  key="result"
                  initial={{opacity: 0, scale: 0.9, y: 20}}
                  animate={{opacity: 1, scale: 1, y: 0}}
                  exit={{opacity: 0, scale: 0.9}}
                  className="bg-white rounded-2xl shadow-xl border border-jeruel-grayblue overflow-hidden sticky top-24"
                >
                  <div className="bg-jeruel-blue p-6 text-center">
                    <span className="text-white/70 font-bold text-xs uppercase tracking-widest">Calculated Result</span>
                    <div className="text-5xl font-bold text-white mt-3 mb-1 tracking-tight">
                      {formatResult(result.value, result.format)}
                    </div>
                    <span className="text-white/60 text-sm font-medium">{result.label}</span>
                  </div>

                  {metric.benchmark && (
                    <div className="p-6">
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-slate-500 uppercase">Benchmark Target</span>
                          <span className="text-xs font-bold bg-jeruel-soft text-jeruel-blue px-2 py-0.5 rounded">
                            {metric.benchmark.type === 'trend' ? 'Trend Analysis' : 'Static Target'}
                          </span>
                        </div>
                        <div className="text-lg font-bold text-slate-800">
                          {metric.benchmark.target}
                          {metric.benchmark.unit && <span className="text-sm text-slate-500 font-normal ml-1">{metric.benchmark.unit}</span>}
                        </div>
                      </div>

                      {metric.benchmark.type !== 'trend' && metric.benchmark.type !== 'none' && (
                        <div className={clsx(
                          "rounded-xl p-4 border flex items-start",
                          getBenchmarkStatus(result.value) === 'good' ? "bg-emerald-50 border-emerald-100" :
                          getBenchmarkStatus(result.value) === 'warning' ? "bg-amber-50 border-amber-100" :
                          "bg-rose-50 border-rose-100"
                        )}>
                          <div className="mt-0.5 mr-3">
                            {getBenchmarkStatus(result.value) === 'good' && <SafeIcon icon={FiCheckCircle} className="text-emerald-500 text-xl" />}
                            {getBenchmarkStatus(result.value) === 'warning' && <SafeIcon icon={FiAlertCircle} className="text-amber-500 text-xl" />}
                            {getBenchmarkStatus(result.value) === 'bad' && <SafeIcon icon={FiAlertCircle} className="text-rose-500 text-xl" />}
                          </div>
                          <div>
                            <h4 className={clsx(
                              "font-bold text-sm mb-1",
                              getBenchmarkStatus(result.value) === 'good' ? "text-emerald-800" :
                              getBenchmarkStatus(result.value) === 'warning' ? "text-amber-800" :
                              "text-rose-800"
                            )}>
                              {getBenchmarkStatus(result.value) === 'good' ? "World Class Performance" :
                               getBenchmarkStatus(result.value) === 'warning' ? "Good / Fair" :
                               "Needs Improvement"}
                            </h4>
                            <p className="text-xs opacity-80 leading-relaxed text-slate-700">
                              {getBenchmarkStatus(result.value) === 'good' ?
                               "Your metric meets or exceeds industry best practices." :
                               "There is potential opportunity for optimization in this area."}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-dashed border-jeruel-grayblue p-8 text-center sticky top-24">
                  <div className="w-16 h-16 bg-jeruel-soft rounded-full flex items-center justify-center mx-auto mb-4 text-jeruel-blue/40">
                    <SafeIcon icon={FiActivity} className="text-3xl" />
                  </div>
                  <h3 className="text-slate-900 font-bold mb-2">Ready to Calculate</h3>
                  <p className="text-slate-500 text-sm">Fill in the values on the left to see your result and benchmark analysis.</p>
                </div>
              )
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-jeruel-grayblue p-6 sticky top-24">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-jeruel-soft rounded-lg text-jeruel-blue">
                    <SafeIcon icon={FiFileText} className="text-xl" />
                  </div>
                  <h3 className="font-bold text-slate-900">Reference Metric</h3>
                </div>
                <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                  This metric is provided for reference and strategic alignment. It typically requires complex data inputs or qualitative assessment that is best handled outside of a simple calculator.
                </p>
                {metric.benchmark && (
                  <div className="border-t border-slate-100 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-slate-500 uppercase">Industry Benchmark</span>
                    </div>
                    <div className="text-xl font-bold text-jeruel-blue">
                      {metric.benchmark.target}
                    </div>
                  </div>
                )}
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CalculatorPage;